import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Coins,
  FileSpreadsheet,
  FileText,
  Globe2,
  HandCoins,
  HeartHandshake,
  Layers,
  RefreshCcw,
  Wallet,
} from 'lucide-react';
import { Button, cn } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { ApiError } from '../../../services/api/client';
import {
  OfferingsAvailableDate,
  OfferingsByChurchReport,
  OfferingsChurchRow,
  OfferingsClassRow,
  OfferingsSummary,
  offeringsReportsService,
} from '../services/offeringsReports.service';

interface OfferingsReportViewProps {
  quarterId: string;
  quarterLabel: string;
}

type ViewMode = 'summary' | 'by-church';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number.isFinite(value) ? value : 0);

const formatDateLabel = (value: string) => {
  if (!value) {
    return '—';
  }

  const dateOnly = value.length >= 10 ? value.slice(0, 10) : value;
  const parsed = new Date(`${dateOnly}T00:00:00`);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
};

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);

const getMostRecentDate = (dates: OfferingsAvailableDate[]): string | null => {
  if (dates.length === 0) {
    return null;
  }

  return dates
    .map((item) => item.date)
    .reduce((latest, current) => (current > latest ? current : latest));
};

const downloadBlob = (content: BlobPart, filename: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 250);
};

const escapeCsvCell = (value: string | number | boolean | null | undefined) => {
  if (value === null || value === undefined) {
    return '""';
  }

  const text = String(value).replace(/"/g, '""');
  return `"${text}"`;
};

const buildCsv = (rows: Array<Array<string | number | boolean | null>>) => {
  const header = '﻿';
  const body = rows.map((row) => row.map(escapeCsvCell).join(';')).join('\r\n');
  return `${header}${body}`;
};

const PRINT_STYLE = `
  body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #0f172a; margin: 0; padding: 24px; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 18px; margin: 24px 0 8px; }
  h3 { font-size: 14px; margin: 12px 0 4px; color: #475569; }
  p.helper { color: #475569; margin: 0 0 18px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 18px; font-size: 12px; }
  thead { background: #f1f5f9; }
  th, td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; }
  td.numeric, th.numeric { text-align: right; font-variant-numeric: tabular-nums; }
  .totals-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; margin-bottom: 18px; }
  .total-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px 14px; }
  .total-card .label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; }
  .total-card .value { font-size: 18px; font-weight: 700; margin-top: 6px; }
  .badge-missions { background: #fff7ed; color: #b45309; border-radius: 999px; padding: 1px 8px; font-size: 11px; font-weight: 600; }
  @media print {
    body { padding: 0; }
    h2 { page-break-after: avoid; }
    table { page-break-inside: avoid; }
  }
`;

const printHtml = (title: string, bodyHtml: string) => {
  const previousTitle = document.title;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  document.body.appendChild(iframe);

  const printDocument = iframe.contentDocument ?? iframe.contentWindow?.document;

  if (!printDocument) {
    document.body.removeChild(iframe);
    return;
  }

  printDocument.open();
  printDocument.write(`<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>${PRINT_STYLE}</style>
</head>
<body>${bodyHtml}</body>
</html>`);
  printDocument.close();

  const cleanup = () => {
    document.title = previousTitle;
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 500);
  };

  const triggerPrint = () => {
    try {
      document.title = title;
      const targetWindow = iframe.contentWindow;
      if (!targetWindow) {
        cleanup();
        return;
      }

      targetWindow.focus();
      targetWindow.print();
    } catch (err) {
      console.error('Falha ao imprimir relatório', err);
    } finally {
      cleanup();
    }
  };

  if (printDocument.readyState === 'complete') {
    setTimeout(triggerPrint, 100);
  } else {
    iframe.addEventListener('load', () => setTimeout(triggerPrint, 100), { once: true });
  }
};

const HighlightCard: React.FC<{
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  value: string;
  helper?: string;
  tone: string;
}> = ({ icon: Icon, label, value, helper, tone }) => (
  <Card className="border-slate-200 bg-white p-4">
    <div className="flex items-start gap-3">
      <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ring-1', tone)}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
        <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
        {helper && <p className="mt-1 text-sm text-slate-500">{helper}</p>}
      </div>
    </div>
  </Card>
);

const SundaySelector: React.FC<{
  options: OfferingsAvailableDate[];
  value: string[];
  onChange: (next: string[]) => void;
}> = ({ options, value, onChange }) => {
  const allDates = useMemo(() => options.map((option) => option.date), [options]);
  const allSelected = value.length === 0;

  const toggleDate = (date: string) => {
    if (value.includes(date)) {
      onChange(value.filter((item) => item !== date));
      return;
    }

    onChange([...value, date]);
  };

  return (
    <Card className="border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">Domingos do trimestre</h3>
          <p className="mt-1 text-xs text-slate-500">
            Selecione os domingos para filtrar o relatório. Se nenhum for marcado, todos os domingos com lançamento serão considerados.
          </p>
        </div>

        <div className="flex flex-col items-end gap-1 text-xs font-semibold">
          <button
            type="button"
            onClick={() => onChange([])}
            className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-700 hover:bg-slate-200"
          >
            Selecionar todos
          </button>
          <button
            type="button"
            onClick={() => onChange([...allDates])}
            className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-700 hover:bg-slate-200"
          >
            Cada domingo
          </button>
        </div>
      </div>

      {options.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-500">
          Nenhum domingo com lançamento de oferta encontrado neste trimestre.
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {options.map((option) => {
            const isSelected = allSelected || value.includes(option.date);
            return (
              <button
                key={option.date}
                type="button"
                onClick={() => toggleDate(option.date)}
                className={cn(
                  'flex items-start justify-between gap-2 rounded-2xl border px-3 py-2 text-left transition',
                  isSelected
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                )}
              >
                <div>
                  <p className="text-sm font-semibold">{formatDateLabel(option.date)}</p>
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">
                    {option.churches_count} igreja(s)
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold">{formatCurrency(option.total_amount)}</p>
                  {option.is_missions_offering && (
                    <span className="mt-1 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                      Missões
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
};

const SummaryView: React.FC<{ data: OfferingsSummary }> = ({ data }) => (
  <section className="space-y-4">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <HighlightCard
        icon={Coins}
        label="Total arrecadado"
        value={formatCurrency(data.totals.gross_amount)}
        helper={`${formatNumber(data.totals.lessons_count)} lançamentos em ${formatNumber(data.totals.sundays_count)} domingo(s)`}
        tone="bg-orange-100 text-orange-700 ring-orange-200"
      />
      <HighlightCard
        icon={HeartHandshake}
        label="Direcionado para missões"
        value={formatCurrency(data.totals.missions_amount)}
        helper="Domingos marcados como oferta missionária"
        tone="bg-rose-100 text-rose-700 ring-rose-200"
      />
      <HighlightCard
        icon={Building2}
        label="Repasse para a sede"
        value={formatCurrency(data.totals.headquarters_amount)}
        helper={`${(data.totals.headquarters_rate * 100).toFixed(0)}% das ofertas das congregações`}
        tone="bg-sky-100 text-sky-700 ring-sky-200"
      />
      <HighlightCard
        icon={Wallet}
        label="Saldo local"
        value={formatCurrency(data.totals.local_amount)}
        helper={`${formatNumber(data.totals.churches_count)} igreja(s) • ${formatNumber(data.totals.classes_count)} classe(s)`}
        tone="bg-emerald-100 text-emerald-700 ring-emerald-200"
      />
    </div>

    <Card className="border-slate-200 bg-white p-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Domingos do recorte</h3>
          <p className="text-sm text-slate-500">Detalhamento por domingo, com missões e repasse para a sede.</p>
        </div>
        <div className="rounded-2xl bg-slate-100 p-2 text-slate-600">
          <Layers size={18} />
        </div>
      </header>

      {data.sundays.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-500">
          Nenhum domingo com lançamento neste recorte.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[600px] text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">Data</th>
                <th className="px-3 py-2 text-right">Bruto</th>
                <th className="px-3 py-2 text-right">Missões</th>
                <th className="px-3 py-2 text-right">Sede</th>
                <th className="px-3 py-2 text-right">Local</th>
                <th className="px-3 py-2 text-right">Lançamentos</th>
              </tr>
            </thead>
            <tbody>
              {data.sundays.map((sunday) => (
                <tr key={sunday.date} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-slate-900">
                    <div className="flex items-center gap-2">
                      <span>{formatDateLabel(sunday.date)}</span>
                      {sunday.is_missions_offering && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                          Missões
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-900">{formatCurrency(sunday.gross_amount)}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{formatCurrency(sunday.missions_amount)}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{formatCurrency(sunday.headquarters_amount)}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{formatCurrency(sunday.local_amount)}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{formatNumber(sunday.lessons_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  </section>
);

const ByChurchView: React.FC<{ data: OfferingsByChurchReport }> = ({ data }) => (
  <section className="space-y-4">
    <Card className="border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
          <Globe2 size={20} />
        </div>
        <div>
          <h3 className="text-lg font-bold text-slate-900">Resumo por nível organizacional</h3>
          <p className="text-sm text-slate-500">
            Bruto {formatCurrency(data.totals.gross_amount)} • Missões {formatCurrency(data.totals.missions_amount)} • Sede {formatCurrency(data.totals.headquarters_amount)} • Local {formatCurrency(data.totals.local_amount)}
          </p>
        </div>
      </div>
    </Card>

    {data.churches.length === 0 ? (
      <Card className="border-slate-200 bg-white p-4 text-center text-sm text-slate-500">
        Nenhuma igreja com lançamento no recorte selecionado.
      </Card>
    ) : (
      data.churches.map((church) => <ChurchSection key={church.church_id} church={church} />)
    )}
  </section>
);

const ChurchSection: React.FC<{ church: OfferingsChurchRow }> = ({ church }) => (
  <Card className="space-y-4 border-slate-200 bg-white p-4">
    <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-bold text-slate-900">{church.church_name}</h3>
          {church.level_name && (
            <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              {church.level_name}
            </span>
          )}
          {church.is_headquarters && (
            <span className="rounded-full bg-sky-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-sky-700">
              Sede
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {formatNumber(church.classes.length)} classe(s) com lançamentos no recorte.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <CompactStat label="Bruto" value={formatCurrency(church.totals.gross_amount)} />
        <CompactStat label="Missões" value={formatCurrency(church.totals.missions_amount)} />
        <CompactStat label="Sede" value={formatCurrency(church.totals.headquarters_amount)} />
        <CompactStat label="Local" value={formatCurrency(church.totals.local_amount)} />
      </div>
    </header>

    <div className="space-y-3">
      {church.classes.map((classRow) => (
        <ClassSection key={classRow.class_id} classRow={classRow} />
      ))}
    </div>
  </Card>
);

const ClassSection: React.FC<{ classRow: OfferingsClassRow }> = ({ classRow }) => (
  <article className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <h4 className="text-sm font-bold text-slate-900">{classRow.class_name}</h4>
      <p className="text-xs font-semibold text-slate-600">
        {formatCurrency(classRow.totals.gross_amount)} • Sede {formatCurrency(classRow.totals.headquarters_amount)} • Local {formatCurrency(classRow.totals.local_amount)}
      </p>
    </div>

    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[520px] text-xs">
        <thead>
          <tr className="text-left uppercase tracking-wide text-slate-500">
            <th className="py-2 pr-3">Domingo</th>
            <th className="py-2 pr-3">Lição</th>
            <th className="py-2 pr-3 text-right">Bruto</th>
            <th className="py-2 pr-3 text-right">Missões</th>
            <th className="py-2 pr-3 text-right">Sede</th>
            <th className="py-2 text-right">Local</th>
          </tr>
        </thead>
        <tbody>
          {classRow.lessons.map((lesson, index) => (
            <tr key={`${lesson.date}-${index}`} className="border-t border-slate-200">
              <td className="py-2 pr-3 font-medium text-slate-900">
                <div className="flex items-center gap-2">
                  <span>{formatDateLabel(lesson.date)}</span>
                  {lesson.is_missions_offering && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-700">
                      Missões
                    </span>
                  )}
                </div>
              </td>
              <td className="py-2 pr-3 text-slate-600">
                {lesson.lesson_number > 0 ? `Lição ${lesson.lesson_number}` : '—'} {lesson.lesson_title ? ` • ${lesson.lesson_title}` : ''}
              </td>
              <td className="py-2 pr-3 text-right font-semibold text-slate-900">{formatCurrency(lesson.gross_amount)}</td>
              <td className="py-2 pr-3 text-right text-slate-600">{formatCurrency(lesson.missions_amount)}</td>
              <td className="py-2 pr-3 text-right text-slate-600">{formatCurrency(lesson.headquarters_amount)}</td>
              <td className="py-2 text-right text-slate-600">{formatCurrency(lesson.local_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </article>
);

const CompactStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-2xl bg-slate-50 px-3 py-2">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-sm font-bold text-slate-900">{value}</p>
  </div>
);

const buildSummaryCsv = (data: OfferingsSummary, byChurch: OfferingsByChurchReport, quarterLabel: string) => {
  const rows: Array<Array<string | number>> = [];
  rows.push(['Relatório de ofertas — Resumo']);
  rows.push(['Trimestre', quarterLabel]);
  rows.push(['Período', `${data.period.starts_on ?? ''} a ${data.period.ends_on ?? ''}`]);
  rows.push([]);
  rows.push(['Indicador', 'Valor']);
  rows.push(['Total bruto', data.totals.gross_amount.toFixed(2)]);
  rows.push(['Missões', data.totals.missions_amount.toFixed(2)]);
  rows.push(['Sede (20%)', data.totals.headquarters_amount.toFixed(2)]);
  rows.push(['Local (80%)', data.totals.local_amount.toFixed(2)]);
  rows.push(['Lançamentos', data.totals.lessons_count]);
  rows.push(['Domingos', data.totals.sundays_count]);
  rows.push(['Igrejas', data.totals.churches_count]);
  rows.push(['Classes', data.totals.classes_count]);
  rows.push([]);
  rows.push(['Domingo', 'Bruto', 'Missões', 'Sede', 'Local', 'Lançamentos', 'Missões?']);
  data.sundays.forEach((sunday) => {
    rows.push([
      formatDateLabel(sunday.date),
      sunday.gross_amount.toFixed(2),
      sunday.missions_amount.toFixed(2),
      sunday.headquarters_amount.toFixed(2),
      sunday.local_amount.toFixed(2),
      sunday.lessons_count,
      sunday.is_missions_offering ? 'Sim' : 'Não',
    ]);
  });
  rows.push([]);
  rows.push(['Por igreja e classe']);
  rows.push(['Igreja', 'Nível', 'Classe', 'Bruto', 'Missões', 'Sede', 'Local', 'Lançamentos']);
  byChurch.churches.forEach((church) => {
    if (church.classes.length === 0) {
      rows.push([
        church.church_name,
        church.level_name ?? '—',
        '—',
        church.totals.gross_amount.toFixed(2),
        church.totals.missions_amount.toFixed(2),
        church.totals.headquarters_amount.toFixed(2),
        church.totals.local_amount.toFixed(2),
        church.totals.lessons_count,
      ]);
      return;
    }

    church.classes.forEach((classRow) => {
      rows.push([
        church.church_name,
        church.level_name ?? '—',
        classRow.class_name,
        classRow.totals.gross_amount.toFixed(2),
        classRow.totals.missions_amount.toFixed(2),
        classRow.totals.headquarters_amount.toFixed(2),
        classRow.totals.local_amount.toFixed(2),
        classRow.totals.lessons_count,
      ]);
    });
  });

  return buildCsv(rows);
};

const buildPrintableHtml = (
  data: OfferingsSummary,
  byChurch: OfferingsByChurchReport,
  quarterLabel: string,
  selectedDates: string[]
) => {
  const periodLabel = data.period.starts_on && data.period.ends_on
    ? `${formatDateLabel(data.period.starts_on)} a ${formatDateLabel(data.period.ends_on)}`
    : 'Trimestre completo';
  const datesLabel = selectedDates.length > 0
    ? selectedDates.map(formatDateLabel).join(', ')
    : 'Todos os domingos com lançamento';

  const totalsHtml = `
    <div class="totals-grid">
      <div class="total-card"><p class="label">Total bruto</p><p class="value">${formatCurrency(data.totals.gross_amount)}</p></div>
      <div class="total-card"><p class="label">Missões</p><p class="value">${formatCurrency(data.totals.missions_amount)}</p></div>
      <div class="total-card"><p class="label">Sede (${(data.totals.headquarters_rate * 100).toFixed(0)}%)</p><p class="value">${formatCurrency(data.totals.headquarters_amount)}</p></div>
      <div class="total-card"><p class="label">Local</p><p class="value">${formatCurrency(data.totals.local_amount)}</p></div>
    </div>`;

  const sundaysHtml = `
    <h2>Domingos do recorte</h2>
    <table>
      <thead>
        <tr><th>Data</th><th class="numeric">Bruto</th><th class="numeric">Missões</th><th class="numeric">Sede</th><th class="numeric">Local</th><th class="numeric">Lançamentos</th></tr>
      </thead>
      <tbody>
        ${data.sundays.map((sunday) => `
          <tr>
            <td>${formatDateLabel(sunday.date)}${sunday.is_missions_offering ? ' <span class="badge-missions">Missões</span>' : ''}</td>
            <td class="numeric">${formatCurrency(sunday.gross_amount)}</td>
            <td class="numeric">${formatCurrency(sunday.missions_amount)}</td>
            <td class="numeric">${formatCurrency(sunday.headquarters_amount)}</td>
            <td class="numeric">${formatCurrency(sunday.local_amount)}</td>
            <td class="numeric">${formatNumber(sunday.lessons_count)}</td>
          </tr>`).join('')}
      </tbody>
    </table>`;

  const churchesHtml = byChurch.churches.map((church) => `
    <h2>${church.church_name} ${church.level_name ? `<small style="font-weight:500;color:#475569;font-size:13px;">— ${church.level_name}</small>` : ''}</h2>
    <p class="helper">Bruto ${formatCurrency(church.totals.gross_amount)} • Missões ${formatCurrency(church.totals.missions_amount)} • Sede ${formatCurrency(church.totals.headquarters_amount)} • Local ${formatCurrency(church.totals.local_amount)}</p>
    ${church.classes.map((classRow) => `
      <h3>${classRow.class_name}</h3>
      <table>
        <thead>
          <tr><th>Domingo</th><th>Lição</th><th class="numeric">Bruto</th><th class="numeric">Missões</th><th class="numeric">Sede</th><th class="numeric">Local</th></tr>
        </thead>
        <tbody>
          ${classRow.lessons.map((lesson) => `
            <tr>
              <td>${formatDateLabel(lesson.date)}${lesson.is_missions_offering ? ' <span class="badge-missions">Missões</span>' : ''}</td>
              <td>${lesson.lesson_number > 0 ? `Lição ${lesson.lesson_number}` : '—'}${lesson.lesson_title ? ` — ${lesson.lesson_title}` : ''}</td>
              <td class="numeric">${formatCurrency(lesson.gross_amount)}</td>
              <td class="numeric">${formatCurrency(lesson.missions_amount)}</td>
              <td class="numeric">${formatCurrency(lesson.headquarters_amount)}</td>
              <td class="numeric">${formatCurrency(lesson.local_amount)}</td>
            </tr>`).join('')}
        </tbody>
      </table>
    `).join('')}
  `).join('');

  return `
    <h1>Relatório de ofertas</h1>
    <p class="helper"><strong>${quarterLabel}</strong> • ${periodLabel} • Domingos: ${datesLabel}</p>
    ${totalsHtml}
    ${sundaysHtml}
    <h2>Por igreja e classe</h2>
    ${byChurch.churches.length === 0 ? '<p class="helper">Nenhuma igreja com lançamento no recorte selecionado.</p>' : churchesHtml}
  `;
};

const OfferingsReportView: React.FC<OfferingsReportViewProps> = ({ quarterId, quarterLabel }) => {
  const [availableDates, setAvailableDates] = useState<OfferingsAvailableDate[]>([]);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [summary, setSummary] = useState<OfferingsSummary | null>(null);
  const [byChurch, setByChurch] = useState<OfferingsByChurchReport | null>(null);
  const [isLoadingDates, setIsLoadingDates] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('summary');

  const loadAvailableDates = useCallback(async () => {
    if (!quarterId) {
      return;
    }

    setIsLoadingDates(true);
    setError(null);

    try {
      const dates = await offeringsReportsService.availableDates(quarterId);
      setAvailableDates(dates);
      const mostRecentDate = getMostRecentDate(dates);
      setSelectedDates(mostRecentDate ? [mostRecentDate] : []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar os domingos do trimestre.');
    } finally {
      setIsLoadingDates(false);
    }
  }, [quarterId]);

  const loadReport = useCallback(async () => {
    if (!quarterId) {
      return;
    }

    setIsLoadingReport(true);
    setError(null);

    try {
      const [summaryData, byChurchData] = await Promise.all([
        offeringsReportsService.summary(quarterId, selectedDates),
        offeringsReportsService.byChurch(quarterId, selectedDates),
      ]);
      setSummary(summaryData);
      setByChurch(byChurchData);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar o relatório de ofertas.');
    } finally {
      setIsLoadingReport(false);
    }
  }, [quarterId, selectedDates]);

  useEffect(() => {
    loadAvailableDates();
  }, [loadAvailableDates]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExportCsv = () => {
    if (!summary || !byChurch) {
      return;
    }

    const csv = buildSummaryCsv(summary, byChurch, quarterLabel);
    downloadBlob(csv, `relatorio-ofertas-${quarterLabel.toLowerCase().replace(/\s+/g, '-')}.csv`, 'text/csv;charset=utf-8;');
  };

  const handleExportPdf = () => {
    if (!summary || !byChurch) {
      return;
    }

    const html = buildPrintableHtml(summary, byChurch, quarterLabel, selectedDates);
    printHtml(`Relatório de ofertas — ${quarterLabel}`, html);
  };

  return (
    <section className="space-y-4">
      <Card className="border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Visualização</h2>
            <p className="text-sm text-slate-500">Alterne entre o resumo enxuto e o detalhamento por igreja e classe.</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setViewMode('summary')}
                className={cn('rounded-full px-3 py-1.5', viewMode === 'summary' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600')}
              >
                Resumo
              </button>
              <button
                type="button"
                onClick={() => setViewMode('by-church')}
                className={cn('rounded-full px-3 py-1.5', viewMode === 'by-church' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600')}
              >
                Por igreja
              </button>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                className="gap-2"
                disabled={!summary || !byChurch || isLoadingReport}
                onClick={handleExportCsv}
              >
                <FileSpreadsheet size={16} />
                Excel
              </Button>
              <Button
                type="button"
                className="gap-2"
                disabled={!summary || !byChurch || isLoadingReport}
                onClick={handleExportPdf}
              >
                <FileText size={16} />
                PDF
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <SundaySelector options={availableDates} value={selectedDates} onChange={setSelectedDates} />

      {error && (
        <Card className="flex items-start gap-3 border-rose-200 bg-rose-50 p-4 text-rose-700">
          <HandCoins size={18} />
          <div className="space-y-2">
            <p className="text-sm font-semibold">{error}</p>
            <button
              type="button"
              onClick={() => {
                loadAvailableDates();
                loadReport();
              }}
              className="inline-flex items-center gap-1 text-xs font-bold text-rose-700 underline"
            >
              <RefreshCcw size={12} /> Tentar novamente
            </button>
          </div>
        </Card>
      )}

      {(isLoadingReport || isLoadingDates) && !summary && (
        <Card className="border-slate-200 bg-white p-4 text-sm text-slate-500">Carregando relatório...</Card>
      )}

      {summary && byChurch && viewMode === 'summary' && <SummaryView data={summary} />}
      {summary && byChurch && viewMode === 'by-church' && <ByChurchView data={byChurch} />}
    </section>
  );
};

export default OfferingsReportView;
